/**
 * Last-resort client error boundary.
 *
 * A single-page app that throws during start-up or navigation leaves a blank
 * page, which for a tool a frightened person may be reaching for is the worst
 * possible failure: no explanation, no way forward. This boundary catches
 * otherwise-unhandled errors and rejections and replaces the page with a plain,
 * legible panel offering a reload and the support address.
 *
 * ## It logs nothing off the device
 *
 * This is KinCode's "error reporting with redaction" (§17 Phase 8), and the
 * redaction is total: there is no reporter, no network call, no analytics. §7 and
 * §10 forbid a surveillance trail on the retrieval journey, and an error reporter
 * is exactly that — it would ship stack traces, URLs and timing off-device for
 * every family member who ever hit a bug. The native console still shows the error
 * to a developer (this handler does not call `preventDefault`); server-side
 * redaction already exists in `functions/src/shared/logger.js`. On the client the
 * only safe amount to send is none.
 *
 * ## It cannot trust the rest of the app
 *
 * By the time this runs, the failure may be the component layer, the router or the
 * stylesheet itself. So the panel is built from plain HTML — no `wa-*` component,
 * which may be unregistered — and carries its own literal styles inline rather
 * than relying on `--kin-*` tokens that may never have loaded. This is the same
 * reasoning that pins the emergency notice outside the brand palette: a safety
 * surface must render whatever else has broken. Literal values here are therefore
 * deliberate, not an oversight of the "no literal colours" rule, which governs the
 * themed application styles.
 */

import { SUPPORT_EMAIL, supportHref } from "../config/support.js";

/** Whether listeners have been attached. Installing twice would double-render. */
let installed = false;

/** Whether the panel is already showing. The first failure wins; later errors
 * (often cascading from the first) must not restart or stack the panel. */
let shown = false;

/**
 * The panel's own styles, scoped by the boundary's id. Inlined so the panel is
 * legible with no application stylesheet. Colours are chosen for contrast on their
 * own (WCAG 2.2 AA), not drawn from the brand palette, because the palette may not
 * have loaded.
 */
const PANEL_STYLES = `
#kin-error-boundary {
  position: fixed;
  inset: 0;
  z-index: 2147483647;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: #f7f7f8;
  color: #111d34;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  line-height: 1.5;
  overflow: auto;
}
#kin-error-boundary .kin-error-card {
  max-width: 32rem;
  width: 100%;
  background: #ffffff;
  border: 1px solid #d5d7dd;
  border-radius: 0.5rem;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 1px 3px rgba(17, 29, 52, 0.12);
}
#kin-error-boundary h1 {
  margin: 0 0 0.75rem;
  font-size: 1.375rem;
}
#kin-error-boundary p {
  margin: 0 0 1rem;
}
#kin-error-boundary a {
  color: #1a3a7a;
}
#kin-error-boundary .kin-error-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.25rem;
}
#kin-error-boundary button {
  font: inherit;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  border: 1px solid #111d34;
  background: #111d34;
  color: #ffffff;
}
#kin-error-boundary button:focus-visible,
#kin-error-boundary a:focus-visible {
  outline: 3px solid #1a3a7a;
  outline-offset: 2px;
}
`;

/**
 * Build the error panel. Pure: constructs and returns the node without touching
 * the document, so it can be unit-tested and its idempotence checked by the caller.
 *
 * @returns {HTMLElement}
 */
export function buildErrorPanel() {
  const style = document.createElement("style");
  style.textContent = PANEL_STYLES;

  const heading = document.createElement("h1");
  heading.id = "kin-error-heading";
  // tabindex so focus can be moved here for screen-reader users; -1 keeps it out
  // of the tab order otherwise.
  heading.tabIndex = -1;
  heading.textContent = "Something went wrong";

  const explain = document.createElement("p");
  explain.textContent =
    "KinCode ran into a problem and could not finish loading this page. Your saved " +
    "contacts are unaffected. Please try reloading; if it keeps happening, get in touch.";

  const contact = document.createElement("p");
  contact.append("You can reach us at ");
  const mail = document.createElement("a");
  mail.href = supportHref("KinCode — something went wrong");
  mail.textContent = SUPPORT_EMAIL;
  contact.append(mail, ".");

  const reload = document.createElement("button");
  reload.type = "button";
  reload.textContent = "Reload the page";
  reload.addEventListener("click", () => {
    // A full reload is the honest recovery: it re-runs start-up from scratch
    // rather than trying to resume a half-broken in-memory state.
    window.location.reload();
  });

  const actions = document.createElement("div");
  actions.className = "kin-error-actions";
  actions.append(reload);

  const card = document.createElement("div");
  card.className = "kin-error-card";
  // A labelled assertive region: the failure is announced, and it is conveyed by
  // heading and text, never by colour alone (WCAG 2.2 AA 1.4.1).
  card.setAttribute("role", "alert");
  card.setAttribute("aria-labelledby", "kin-error-heading");
  card.append(heading, explain, contact, actions);

  const panel = document.createElement("div");
  panel.id = "kin-error-boundary";
  panel.append(style, card);
  return panel;
}

/**
 * Show the panel, once. Subsequent calls are ignored so a cascade of errors does
 * not stack panels or steal focus repeatedly.
 *
 * @param {Document} [doc] Injectable for testing; defaults to the live document.
 * @returns {boolean} True if the panel was shown by this call, false if already shown.
 */
export function showErrorBoundary(doc = document) {
  if (shown) {
    return false;
  }
  shown = true;
  const panel = buildErrorPanel();
  doc.body.append(panel);
  // Move focus to the heading so a screen-reader user is taken to the message
  // rather than left wherever the now-broken page had them.
  const heading = panel.querySelector("#kin-error-heading");
  if (heading instanceof HTMLElement) {
    heading.focus();
  }
  return true;
}

/**
 * Attach the global handlers. Safe to call once; a second call is a no-op.
 *
 * Deliberately does not call `preventDefault`, so uncaught errors still reach the
 * browser console for a developer. The boundary adds a user-facing recovery; it
 * does not suppress diagnostics.
 *
 * @param {Window} [win] Injectable for testing; defaults to the live window.
 * @returns {void}
 */
export function installErrorBoundary(win = window) {
  if (installed) {
    return;
  }
  installed = true;

  win.addEventListener("error", (event) => {
    // Ignore resource-load failures (a missing image or stylesheet bubbles an
    // `error` event to the window with no `.error` and a non-window target). Those
    // are not app crashes and must not blank the page. Only a genuine script error
    // — which carries an Error — triggers the boundary.
    if (event instanceof ErrorEvent && event.error) {
      showErrorBoundary(win.document);
    }
  });

  win.addEventListener("unhandledrejection", () => {
    showErrorBoundary(win.document);
  });
}
